import ClearIcon from '@mui/icons-material/Clear';
import { Box, IconButton, TextField, InputAdornment, FormControl } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { selectFilterAction, selectFilterRemoveAction } from 'store/actions/filterActions';
import { changePageAction } from 'store/actions/dashboardActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import CloseIcon from '@mui/icons-material/Close';

interface SimpleIdNameOption {
  id: number | string;
  name: string;
}

type ISearchHeaderCellOptions = SimpleIdNameOption[];

type SearchHeaderCellProps = {
  field: string;
  options?: ISearchHeaderCellOptions;
  headerName: string;
  disabled?: boolean;
};

const SearchHeaderCell = React.memo((props: SearchHeaderCellProps) => {
  const { field, options = [], headerName, disabled = false } = props;

  const { dispatch } = useAppState();
  const [t] = useTranslation();
  const filter = useSelector((state) => state.filter);
  const searchValue = filter.selected?.[field]?.[0] ?? '';
  const [inputValue, setInputValue] = useState(searchValue);

  // ✅ Conditionally add “ALL” option
  const mergedOptions = useMemo(() => {
    if (field === 'otp') {
      return [{ id: 'ALL', name: 'ALL' }, ...options];
    }
    return options;
  }, [options, field]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      searchUpdate(field, inputValue);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [inputValue]);

  useEffect(() => {
    setInputValue(searchValue);
  }, [searchValue]);

  const searchType = useCallback(() => {
    switch (field) {
      case 'otp':
      case 'label':
        // case 'clients':
        // case 'overdue_by':
        return 'select';
      case 'forecast_date':
      case 'delivery_date':
        return 'date';
      default:
        return 'text';
    }
  }, [field]);

  const searchUpdate = useCallback(
    (field: string, value: any) => {
      if (field && value !== '') dispatch(selectFilterAction({ [field]: [value] }));
      else {
        dispatch(selectFilterRemoveAction({ [field]: [] }));
        if (field === 'otp') {
          dispatch(changePageAction(0));
        }
      }
    },
    [dispatch]
  );

  const renderInput = () => {
    if (searchType() === 'select' && mergedOptions?.length > 0) {
      const selectedOption = mergedOptions.find((opt) => String(opt.id) === String(inputValue)) || null;

      return (
        <FormControl fullWidth>
          <label htmlFor={`autocomplete_search_${field}`}>{t(headerName)}</label>
          <Autocomplete
            id={`autocomplete_${field}`}
            size="small"
            disabled={disabled}
            options={mergedOptions}
            value={selectedOption}
            onChange={(_, newValue) => {
              setInputValue(newValue ? String(newValue.id) : '');
            }}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: <>{params.InputProps.endAdornment}</>,
                }}
              />
            )}
            sx={{
              // Similar to p: 0.5 on .MuiSelect-select
              '.MuiAutocomplete-inputRoot': {
                padding: '2px !important',
              },

              // Dropdown arrow icon styling (similar to .MuiSelect-icon)
              '.MuiAutocomplete-popupIndicator': {
                position: 'absolute',
                right: 2,
                zIndex: 2,
              },

              // Clear icon (optional)
              '.MuiAutocomplete-clearIndicator': {
                zIndex: 3,
                right: 20,
              },
            }}
            ListboxProps={{
              style: {
                // maxHeight: 250,
                // padding: 0.5,
                overflowY: 'auto',
              },
            }}
          />
        </FormControl>
      );
    }

    // Default input
    return (
      <>
        <label htmlFor={`input_header_${field}`}>{t(headerName)}</label>
        <TextField
          onChange={(newValue) => {
            setInputValue(newValue.target.value);
          }}
          id={`input_header_${field}`}
          value={inputValue}
          type={searchType()}
          required
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            endAdornment: (
              <IconButton
                sx={{ visibility: inputValue ? 'visible' : 'hidden', padding: 0 }}
                onClick={() => setInputValue('')}
                color="primary"
              >
                <ClearIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            ),
            style: { fontSize: 12 },
            className: 'input_head_grid_search',
          }}
          sx={{
            padding: 0,
            fontSize: '0.5rem',
            '&.Mui-focused .MuiIconButton-root': { color: 'primary.main' },
          }}
        />
      </>
    );
  };

  return <Box sx={{ display: 'block', width: '100%' }}>{renderInput()}</Box>;
});

export default SearchHeaderCell;
